import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import deleteIcon from '/deleteCrop.svg';
import trashIcon from '/trashHelp.svg';
import emptyIcon from '/emptyTable.svg';

import Button from 'components/Button';
import { unMaskInputValue } from 'utils/helpers';
import { cn } from 'utils/cn';
import { formatSwissNumber } from 'utils/formatNumber';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import SupplyPlanTableColumns from './SupplyPlanTableColumns';

interface Props {
  isPDF?: boolean;
}

const SupplyPlanTable = ({ isPDF }: Props) => {
  const {
    setActiveTab,
    fields,
    crops,
    setCrops,
    setFields,
    disabledState,
    setDisabledState,
  } = useCroptimizer();

  const { t } = useTranslation();
  const [modal, setModal] = useState<string | null>(null);

  const onDelete = () => {
    const getIndexof = fields.findIndex((field) => field.title === modal);
    const filteredData = fields.filter((field) => field.title !== modal);
    const notFilteredData = fields.filter((field) => field.title === modal);
    const filteredCrops = crops.filter(
      (crop) => crop.fieldName !== notFilteredData?.[0]?.title,
    );
    setCrops(filteredCrops);
    setFields(filteredData);
    setActiveTab(fields?.[getIndexof - 1]?.id ?? fields?.[getIndexof + 1]?.id);
    setModal(null);
  };

  const onDeleteFromSupply = () => {
    const notFilteredData = fields.filter((field) => field.title === modal);
    const filteredCrops = crops.filter(
      (crop) => crop.fieldName !== notFilteredData?.[0]?.title,
    );
    const filteredDisabledState = disabledState.filter(
      (state) => state !== modal,
    );
    setCrops(filteredCrops);
    setModal(null);
    setDisabledState(filteredDisabledState);
  };

  const getStatus = (currentph: string, targetph: string, length: number) => {
    if (Number(currentph) < Number(targetph)) {
      return t('improving');
    } else if (length > 1) {
      return t('maintenance') + ' ' + length + ' ' + t('years');
    } else {
      return t('maintenance') + ' ' + length + ' ' + t('year');
    }
  };

  const getTotal = useMemo(() => {
    let area = 0;
    let productTotal = 0;
    let cost = 0;
    let gains = 0;
    let roi = 0;

    if (crops) {
      crops.forEach((crop) => {
        area += Number(crop.area);
        productTotal += Number(crop.productmt);
        cost += Number(crop.cost);
        gains += Number(crop.gains);
        roi += Number(crop.roi);
      });
    }

    return {
      area,
      productTotal,
      cost,
      gains,
      roi,
    };
  }, [crops]);

  const ColumnHeader = ({ children }: { children: React.ReactNode }) => (
    <td className={cn('px-1 py-3 text-center', isPDF ? 'text-xs' : 'text-sm')}>
      {children}
    </td>
  );

  const TotalColumnHeader = ({ children }: { children: React.ReactNode }) => (
    <td
      className={cn(
        'bg-[#FAFAFA] border-none px-1 py-4 text-center font-medium',
        isPDF ? 'text-xs' : 'text-sm',
      )}
    >
      {children}
    </td>
  );

  return (
    <div className="w-full overflow-scroll">
      <table className="w-full table-auto text-sm border-collapse border-none bg-white">
        <SupplyPlanTableColumns isPDF={isPDF} />

        <tbody>
          {crops.length ? (
            <>
              {crops.map((data, index) => (
                <tr key={index} className="border-t border-muted">
                  <ColumnHeader>{index + 1}</ColumnHeader>
                  <ColumnHeader>{data.fieldName}</ColumnHeader>
                  <ColumnHeader>
                    {data.tableData.map((el, i) => (
                      <span key={el.id}>
                        {i > 0 && ', '}
                        {el.name}
                      </span>
                    ))}
                  </ColumnHeader>
                  <ColumnHeader>{data.productType}</ColumnHeader>
                  <ColumnHeader>{formatSwissNumber(Number(data.area))}</ColumnHeader>
                  <ColumnHeader>
                    {formatSwissNumber(Number(data.mthaDose))}
                  </ColumnHeader>
                  <ColumnHeader>
                    {formatSwissNumber(Number(data.productmt))}
                  </ColumnHeader>
                  <ColumnHeader>
                    {unMaskInputValue(data.currentph)}
                  </ColumnHeader>
                  <ColumnHeader>
                    {getStatus(
                      data.currentph,
                      data.targetph,
                      data.tableData.length,
                    )}
                  </ColumnHeader>
                  <ColumnHeader>{unMaskInputValue(data.targetph)}</ColumnHeader>
                  <ColumnHeader>{formatSwissNumber(data.cost)}</ColumnHeader>
                  <ColumnHeader>{formatSwissNumber(data.gains)}</ColumnHeader>
                  <td
                    className={cn('px-1 py-3  text-center', {
                      'text-xs': isPDF,
                      'text-sm': !isPDF,
                    })}
                  >
                    {formatSwissNumber(data.roi)}
                  </td>

                  {!isPDF && (
                    <td className="pr-6 pl-1 py-3">
                      <button
                        onClick={() => setModal(data.fieldName)}
                        className="w-[18px] h-[18px]"
                      >
                        <img src={deleteIcon} alt="" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              <tr>
                <td
                  className={cn(
                    'bg-[#FAFAFA] border-none py-4 text-center font-medium',
                    isPDF ? 'px-1 text-xs' : 'pl-6 pr-1 text-sm',
                  )}
                  colSpan={2}
                >
                  Total
                </td>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>
                  {formatSwissNumber(getTotal.area)}
                </TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>
                  {formatSwissNumber(Number(getTotal.productTotal))}
                </TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>
                  {formatSwissNumber(getTotal.cost)}
                </TotalColumnHeader>
                <TotalColumnHeader>
                  {formatSwissNumber(getTotal.gains)}
                </TotalColumnHeader>
                <TotalColumnHeader>
                  {formatSwissNumber(getTotal.roi)}
                </TotalColumnHeader>

                {!isPDF && (
                  <td className="bg-[#FAFAFA] border-none px-1 py-4"></td>
                )}
              </tr>
            </>
          ) : (
            <>
              <tr>
                <td colSpan={13}>
                  <div className="h-[288px] flex justify-center items-center flex-col">
                    <div>
                      <img src={emptyIcon} alt="" />
                    </div>
                    <div className="mt-6 font-medium text-secondaryText">
                      {t('noFieldsTable')}
                    </div>
                  </div>
                </td>
              </tr>

              <tr>
                <td
                  className={cn(
                    'bg-[#FAFAFA] border-none py-4 text-center font-medium',
                    isPDF ? 'pl-6 pr-1 text-xs' : 'text-sm px-1',
                  )}
                  colSpan={2}
                >
                  Total
                </td>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>
                <TotalColumnHeader>-</TotalColumnHeader>

                {!isPDF && (
                  <td className="bg-[#FAFAFA] border-none px-1 py-4"></td>
                )}
              </tr>
            </>
          )}
        </tbody>
      </table>

      {!!modal && (
        <div className="fixed z-50 top-0 left-0 right-0 bottom-0 bg-[rgba(0,0,0,0.5)] flex items-center justify-center h-screen w-screen">
          <div className="bg-white p-8 md:p-4 rounded-2xl flex w-[610px] flex-col items-center md:max-w-80 text-center">
            <div
              onClick={() => setModal(null)}
              className="flex justify-end items-end w-full cursor-pointer"
            >
              <img src={trashIcon} alt="" />
            </div>
            <div className="text-2xl font-bold md:text-lg mb-3 text-black">
              {t('delete')}
            </div>
            <div className="text-secondaryText mb-6">
              {t('whereWouldYouLikeDelete')}
            </div>
            <div className="flex items-center w-full md:flex-col">
              <Button
                className="w-6/12 mr-2 md:w-full md:mr-0"
                btnType="secondary"
                onClick={onDeleteFromSupply}
              >
                {t('supplyDelete')}
              </Button>
              <Button onClick={onDelete} className="w-6/12 md:w-full md:mt-2">
                {t('supplyandcrop')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplyPlanTable;
